// src/pages/basicinfo.tsx
'use client';

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase, currentUser } from "@/lib/supabase";
import { markCompleted, getStepIndexById, steps } from "@/lib/onboarding";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

export default function BasicInfo() {
  const nav = useNavigate();

  const [form, setForm] = useState({
    name: "",
    dob: "",           // YYYY-MM-DD
    phone: "+44",      // starts with +44
    height: "",
    unit: "cm",
    weight: "",
    gender: "",
    phoneConfirmed: false,
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Calculate age from DOB
  const calculateAge = (dob: string) => {
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const next = async () => {
    setErr(null);
    setSaving(true);

    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in");

      // BLOCK UNDER 16
      if (!form.dob) throw new Error("Please enter your date of birth");
      const age = calculateAge(form.dob);
      if (age < 16) {
        setErr("You must be at least 16 years old to use Input.");
        setSaving(false);
        return;
      }

      // Validate required fields
      if (!form.name || !form.gender || form.phone.length < 10 || !form.height || !form.weight) {
        throw new Error("Please fill in all required fields");
      }

      const heightCm = form.unit === "in" ? Number(form.height) * 2.54 : Number(form.height);

      const supabase = getSupabase();

      const { error } = await supabase.from("clients").upsert(
        {
          user_id: user.id,
          name: form.name.trim(),
          date_of_birth: form.dob,
          age,
          phone: form.phone,                    // ← perfect E.164 format
          height_in_cm: heightCm || null,
          weight_in_kg: Number(form.weight) || null,
          gender: form.gender.toLowerCase(),
          email_address: user.email,
        },
        { onConflict: "user_id" }
      );

      if (error) throw error;

      markCompleted("basicInfo");
      const nextIdx = getStepIndexById("basicInfo") + 1;
      nav(steps[nextIdx]?.path || "/activity-level");
    } catch (e: any) {
      setErr(e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout>
      <div className="absolute top-4 left-4 z-10">
  <button
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#0f172a] transition-colors"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
</div>
      
      <div className="max-w-md mx-auto space-y-8 text-center">
        <div>
          <h1 className="text-3xl font-black text-[#0f172a]">Basic Info</h1>
          <p className="text-gray-500 mt-2">We need a few details to get started</p>
        </div>

        <div className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-2">Full Name</label>
            <input
              required
              type="text"
              placeholder="John Doe"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full h-14 px-4 bg-gray-800 border border-gray-700 rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-green-400 focus:ring-2 focus:ring-green-400/30 transition"
            />
          </div>

          {/* Date of Birth — blocks under 16 */}
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-2">
              Date of Birth
            </label>
            <input
              required
              type="date"
              value={form.dob}
              onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split("T")[0]}
              className="w-full h-14 px-4 bg-gray-800 border border-gray-700 rounded-xl text-[#0f172a] focus:border-green-400 focus:ring-2 focus:ring-green-400/30 transition"
            />
            <p className="text-xs text-gray-500 mt-2 text-left">
              You must be 16 or older to use Input
            </p>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-2">Gender</label>
            <div className="grid grid-cols-2 gap-4">
              {["Male", "Female"].map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, gender: g }))}
                  className={`py-4 rounded-2xl font-bold text-lg transition-all ${
                    form.gender === g
                      ? "bg-green-400 text-[#0f172a] shadow-lg shadow-green-400/30"
                      : "bg-gray-800 text-gray-600 border border-gray-700 hover:bg-gray-700"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* Phone — International */}
{/* Phone — UK only, idiot-proof, strips leading 0 */}
<div>
  <label className="block text-left text-sm font-medium text-gray-600 mb-2">
    Phone Number (UK)
  </label>
  <PhoneInput
    country="gb"
    onlyCountries={["gb"]}
    preferredCountries={["gb"]}
    value={form.phone}
    onChange={(phone) => {
      let cleaned = phone || "";

      // Remove leading 0 after 44
      if (cleaned.startsWith("440")) {
        cleaned = cleaned.substring(2);
      }

      // Remove any extra leading zeros
      cleaned = cleaned.replace(/^0+/, "");

      // Ensure starts with 44
      if (!cleaned.startsWith("44")) {
        cleaned = "44" + cleaned;
      }

      setForm(f => ({ ...f, phone: "+" + cleaned }));
    }}
    placeholder="7123 456 789"
    inputProps={{ required: true }}
    containerClass="w-full"
    inputClass="!w-full !h-14 !bg-gray-800 !border !border-gray-700 !rounded-xl !text-[#0f172a] !text-base !pl-16 focus:!border-green-400 focus:!ring-2 focus:!ring-green-400/30"
    buttonClass="!bg-gray-800 !border !border-gray-700 !rounded-l-xl !h-14"
    dropdownClass="hidden"
  />
  <p className="text-xs text-gray-500 mt-2 text-left">
    We'll send you a verification code via WhatsApp
  </p>
</div>

      {/* Phone confirmation checkbox */}
<div className="mt-4">
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={form.phoneConfirmed}
      onChange={(e) => setForm(f => ({ ...f, phoneConfirmed: e.target.checked }))}
      className="w-5 h-5 text-green-400 bg-gray-800 border-gray-700 rounded focus:ring-green-400"
    />
    <span className="text-sm text-gray-600">
      I confirm my phone number is correct: <span className="font-medium text-[#0f172a]">{form.phone || "not entered"}</span>
    </span>
  </label>
</div>

          {/* Height */}
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-2">Height</label>
            <div className="flex gap-3">
              <input
                required
                type="number"
                placeholder="180"
                value={form.height}
                onChange={(e) => setForm(f => ({ ...f, height: e.target.value }))}
                className="flex-1 h-14 px-4 bg-gray-800 border border-gray-700 rounded-xl text-[#0f172a] focus:border-green-400 focus:ring-2 focus:ring-green-400/30"
              />
              <select
                value={form.unit}
                onChange={(e) => setForm(f => ({ ...f, unit: e.target.value }))}
                className="w-24 h-14 bg-gray-800 border border-gray-700 rounded-xl px-4 text-[#0f172a] focus:border-green-400"
              >
                <option value="cm">cm</option>
                <option value="in">in</option>
              </select>
            </div>
          </div>

          {/* Weight */}
          <div>
            <label className="block text-left text-sm font-medium text-gray-600 mb-2">Weight (kg)</label>
            <input
              required
              type="number"
              placeholder="75"
              value={form.weight}
              onChange={(e) => setForm(f => ({ ...f, weight: e.target.value }))}
              className="w-full h-14 px-4 bg-gray-800 border border-gray-700 rounded-xl text-[#0f172a] placeholder-gray-500 focus:border-green-400 focus:ring-2 focus:ring-green-400/30"
            />
          </div>
        </div>

        {/* NEXT BUTTON — BLOCKS UNDER 16 */}
      <button
          onClick={next}
      disabled={
  saving ||
  !form.name ||
  !form.dob ||
  calculateAge(form.dob) < 16 ||
  !form.gender ||
  form.phone.length < 13 ||           // ← +44 + 10 digits = 13 minimum
  !form.phone.startsWith("+44") ||    // ← must be UK
  !form.height ||
  !form.weight ||
  !form.phoneConfirmed                // ← your new checkbox
}
          className="w-full bg-green-400 hover:bg-green-300 text-[#0f172a] font-bold text-xl py-5 rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Continue"}
        </button>

        {err && (
          <div className="mt-4 p-4 bg-red-900/20 border border-red-500/40 rounded-xl text-red-300 text-center">
            <p className="font-medium">{err}</p>
          </div>
        )}

        <ProgressBar step={3} total={10} />
      </div>
    </AuthLayout>
  );
}
